import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler, ErreurApi } from "../api/client";
import { fcfa, useApiEnveloppe } from "../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis } from "../composants/Avis";
import { Tableau, Tuiles } from "../composants/Tableau";
import { ChampMontant } from "../composants/ChampMontant";

const EtatStockSchema = z.object({
  articles: z.array(
    z.object({
      article: z.object({
        reference: z.string(),
        designation: z.string(),
        prixUnitaire: z.number(),
        seuilAlerte: z.number(),
        unite: z.string().nullable(),
      }),
      entrees: z.number(),
      sorties: z.number(),
      stockCourant: z.number(),
      valorisation: z.number(),
      enRupture: z.boolean(),
      sousSeuil: z.boolean(),
    }),
  ),
  valorisationTotale: z.number(),
  ruptures: z.array(z.string()),
  sousSeuil: z.array(z.string()),
});

const AjoutArticleSchema = z.object({ id: z.string(), alertes: z.array(z.string()) });

export function StockClient({ dossierId }: { dossierId: string }) {
  const qc = useQueryClient();
  const [reference, setReference] = useState("");
  const [designation, setDesignation] = useState("");
  const [prix, setPrix] = useState("");
  const [seuil, setSeuil] = useState("");
  const [alertes, setAlertes] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  const q = useApiEnveloppe(["stock", dossierId], `/dossiers/${dossierId}/stock`, EtatStockSchema);

  async function ajouter(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setAlertes([]);

    try {
      const r = await appeler(`/dossiers/${dossierId}/stock/articles`, AjoutArticleSchema, {
        method: "POST",
        body: JSON.stringify({
          reference,
          designation,
          prixUnitaire: Number(prix.replace(/\s/g, "").replace(",", ".")) || 0,
          seuilAlerte: Number(seuil.replace(/\s/g, "").replace(",", ".")) || 0,
          unite: null,
        }),
      });

      // Anti-doublon : les alertes sont montrées, l'article est enregistré.
      setAlertes(r.alertes);
      setReference("");
      setDesignation("");
      setPrix("");
      void qc.invalidateQueries({ queryKey: ["stock", dossierId] });
    } catch (err) {
      setErreur(err instanceof ErreurApi ? (err.motif ?? err.message) : "Enregistrement impossible.");
    }
  }

  if (q.isLoading) return <EtatChargement quoi="du stock" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  const etat = q.data?.valeur;

  return (
    <div>
      <section>
        <h3>Ajouter un article</h3>
        <form onSubmit={ajouter} className="saisie-journal">
          <label>
            Référence
            <input value={reference} onChange={(e) => setReference(e.target.value)} required />
          </label>
          <label className="large">
            Désignation
            <input value={designation} onChange={(e) => setDesignation(e.target.value)} required />
          </label>
          <div>
            <ChampMontant
              label="Prix unitaire"
              valeur={prix}
              onChange={setPrix}
              confirme={false}
              onConfirmer={() => {}}
            />
          </div>
          <label>
            Seuil d'alerte
            <input
              type="text"
              inputMode="numeric"
              value={seuil}
              onChange={(e) => setSeuil(e.target.value)}
            />
          </label>
          <button type="submit">Enregistrer</button>
        </form>

        {alertes.length > 0 && (
          <div className="alerte alerte--info">
            {alertes.map((a, i) => (
              <p key={i}>{a}</p>
            ))}
          </div>
        )}
        {erreur && <p className="alerte" role="alert">{erreur}</p>}
      </section>

      {etat && (
        <>
          <Tuiles
            tuiles={[
              { titre: "Articles", valeur: etat.articles.length },
              { titre: "Valorisation", valeur: fcfa(etat.valorisationTotale) },
              { titre: "Ruptures", valeur: etat.ruptures.length, alerte: etat.ruptures.length > 0 },
              {
                titre: "Sous le seuil",
                valeur: etat.sousSeuil.length,
                alerte: etat.sousSeuil.length > 0,
              },
            ]}
          />

          {(etat.ruptures.length > 0 || etat.sousSeuil.length > 0) && (
            <section>
              <h3>Alertes de rupture</h3>
              <ul className="alertes">
                {etat.ruptures.map((r) => (
                  <li key={r}>Rupture : {r}</li>
                ))}
                {etat.sousSeuil.map((s) => (
                  <li key={s}>Seuil atteint : {s}</li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3>Inventaire</h3>
            <Tableau
              colonnes={[
                { cle: "r", titre: "Référence", rendu: (a) => a.article.reference },
                { cle: "d", titre: "Désignation", rendu: (a) => a.article.designation },
                { cle: "e", titre: "Entrées", montant: true, rendu: (a) => a.entrees },
                { cle: "s", titre: "Sorties", montant: true, rendu: (a) => a.sorties },
                {
                  cle: "c",
                  titre: "Stock",
                  montant: true,
                  rendu: (a) => (
                    <span className={a.enRupture ? "non" : a.sousSeuil ? "montant-suspect" : undefined}>
                      {a.stockCourant}
                    </span>
                  ),
                },
                { cle: "pu", titre: "Prix unitaire", montant: true, rendu: (a) => fcfa(a.article.prixUnitaire) },
                { cle: "v", titre: "Valorisation", montant: true, rendu: (a) => fcfa(a.valorisation) },
              ]}
              lignes={etat.articles}
              cle={(a) => a.article.reference}
              vide="Aucun article à l'inventaire."
            />
          </section>
        </>
      )}

      <ListeAvis avis={q.data?.avis ?? []} />
    </div>
  );
}

// ===========================================================================
// VENTES
// ===========================================================================

const VenteSchema = z.object({
  id: z.string(),
  numero: z.string(),
  total: z.number(),
  lignes: z.array(
    z.object({
      reference: z.string(),
      designation: z.string(),
      quantite: z.number(),
      prixUnitaire: z.number(),
    }),
  ),
});

export function VentesClient({ dossierId }: { dossierId: string }) {
  const qc = useQueryClient();
  const [entreprise, setEntreprise] = useState(false);
  const [nom, setNom] = useState("");
  const [ncc, setNcc] = useState("");
  const [email, setEmail] = useState("");
  const [moyen, setMoyen] = useState("Espèces");
  const [reference, setReference] = useState("");
  const [quantite, setQuantite] = useState("1");
  const [recu, setRecu] = useState<z.infer<typeof VenteSchema> | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  async function vendre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);

    try {
      const r = await appeler(`/dossiers/${dossierId}/stock/ventes-directes`, VenteSchema, {
        method: "POST",
        body: JSON.stringify({
          date: new Date().toISOString().slice(0, 10),
          acheteurEntreprise: entreprise,
          acheteurNom: nom,
          acheteurNcc: entreprise ? ncc : null,
          acheteurEmail: entreprise ? email : null,
          moyenPaiement: moyen,
          lignes: [{ reference, quantite: Number(quantite) || 1 }],
        }),
      });

      setRecu(r);
      setReference("");
      setQuantite("1");
      void qc.invalidateQueries({ queryKey: ["stock", dossierId] });
    } catch (err) {
      setErreur(err instanceof ErreurApi ? (err.motif ?? err.message) : "Vente impossible.");
    }
  }

  return (
    <div>
      <section>
        <h3>Vente directe</h3>
        <p className="resume">
          La vente décrémente le stock et produit un reçu. Pour une facture
          normalisée, éditez-la sur la plateforme FNE puis importez le PDF et le
          numéro FNE — <strong>il n'y a pas d'API DGI</strong>.
        </p>

        <form onSubmit={vendre} className="saisie-journal">
          <label className="filtre--case">
            <input
              type="checkbox"
              checked={entreprise}
              onChange={(e) => setEntreprise(e.target.checked)}
            />
            L'acheteur est une entreprise
          </label>

          <label className="large">
            Nom de l'acheteur
            <input value={nom} onChange={(e) => setNom(e.target.value)} required />
          </label>

          {entreprise && (
            <>
              <label>
                NCC
                <input value={ncc} onChange={(e) => setNcc(e.target.value)} required />
              </label>
              <label>
                Courriel
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
            </>
          )}

          <label>
            Référence article
            <input value={reference} onChange={(e) => setReference(e.target.value)} required />
          </label>

          <label>
            Quantité
            <input
              type="text"
              inputMode="numeric"
              value={quantite}
              onChange={(e) => setQuantite(e.target.value)}
            />
          </label>

          <label>
            Moyen de paiement
            <select value={moyen} onChange={(e) => setMoyen(e.target.value)}>
              {["Espèces", "Mobile money", "Carte", "Chèque", "Virement"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          <button type="submit">Enregistrer la vente</button>
        </form>

        {erreur && <p className="alerte" role="alert">{erreur}</p>}
      </section>

      {recu && (
        <section className="recu">
          <h3>Reçu {recu.numero}</h3>
          <Tableau
            colonnes={[
              { cle: "r", titre: "Référence", rendu: (l) => l.reference },
              { cle: "d", titre: "Désignation", rendu: (l) => l.designation },
              { cle: "q", titre: "Quantité", montant: true, rendu: (l) => l.quantite },
              { cle: "pu", titre: "Prix unitaire", montant: true, rendu: (l) => fcfa(l.prixUnitaire) },
              {
                cle: "t",
                titre: "Total",
                montant: true,
                rendu: (l) => fcfa(l.quantite * l.prixUnitaire),
              },
            ]}
            lignes={recu.lignes}
            cle={(l) => l.reference}
          />
          <p className="recu__total">Total : {fcfa(recu.total)}</p>
        </section>
      )}
    </div>
  );
}
